import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, message, notification, Row, Skeleton, Spin, Table } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ajaxService, awsService, containerService, dropdownService, loadingPlanService } from "../../../services";
import ImageUploader from "../../../components/ImageUploader";
import { CONTAINERS } from "../../../constants";
import { useNavigate } from "react-router-dom";
import VehicleDropDown from "./VehicleDropdown";
import MaterialLikeInput from "../../../components/MaterialLikeInput";
import MaterialLikeSelect from "../../../components/MaterialLikeSelect";
import MaterialLikeDatePicker from "../../../components/MaterialLikeDatePicker";
import { changeDocumentTitle } from "../../../utils/title";
import { debounce } from 'lodash';

function validate(value) {
  return value === null || value === "" ? "-" : value;
}

function LoadingAdd() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [containerIDLoad, setContainerIDLoad] = useState(false)
  const [carLoading, setCarLoading] = useState(false);
  const [shippingLine, setShippingLine] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [newCarOptions, setNewCarOptions] = useState([]);
  const [reloadCarOptions, setReloadCarOptions] = useState([]);
  const [containerSize, setContainerSize] = useState([]);
  const [type, setType] = useState(null)
  const [tableData, setTableData] = useState([]);
  const [form] = Form.useForm();
  const [container_number, setContainer_number] = useState("");

  const [fileList, setFileList] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [bookingMessage, setBookingMessage] = useState(null)
  const [bookingExists, setBookingExists] = useState(null)
  const [bookingLoading, setBookingLoading] = useState(false)

  const [bookingInput, setBookingInput] = useState("");

  const columns = [
    {
      title: "Vehicle",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Chassis Number",
      dataIndex: "chasis_no",
      key: "chasis_no",
    },
    {
      title: (
        <div>
          <DeleteOutlined style={{ fontSize: "18px" }} />
        </div>
      ),
      key: "Delete",
      render: (text, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleDelete(record.id)}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  const handleChange = (info) => {
    let updatedFileList = [...info.fileList];
    setFileList(updatedFileList);
  };

  // API call function
  const fetchCarOptions = async (query, setOptions) => {
    if (!query) {
      setOptions([]);
      return;
    }
    try {
      setCarLoading(true)
      const response = await dropdownService.fetchVehiclesOnSearch({ query });
      setCarLoading(false)
      if (response.success) {
        setOptions(
          response.data.map((car) => ({
            vehicle: car?.vehicle,
            value: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.container_no)} || ${validate(car?.customer_name)}`,
            label: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.container_no)} || ${validate(car?.customer_name)}`,
            data: car,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching vehicle options:", error);
      setOptions([]);
    }
  };
  // API call function
  const fetchReloadCarOptions = async (query, setOptions) => {
    if (!query) {
      setOptions([]);
      return;
    }
    try {
      setCarLoading(true)
      const response = await dropdownService.fetchReloadVehiclesOnSearch({ query });
      setCarLoading(false)
      if (response.success) {
        setOptions(
          response.data.map((car) => ({
            value: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.container_no)} || ${validate(car?.customer_name)}`,
            label: `${validate(car?.vehicle)} || ${validate(car?.chasis_no)} || ${validate(car?.year)} || ${validate(car?.container_no)} || ${validate(car?.customer_name)}`,
            data: car,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching vehicle options:", error);
      setOptions([]);
    }
  };

  const handleSelect = (value, option) => {
    const { data } = option;
    const selectedCar = {
      id: data.id,
      name: data.vehicle,
      chasis_no: data.chasis_no,
    };

    setTableData((prev) => {
      const exists = prev.some((car) => car.chasis_no === selectedCar.chasis_no);
      if (exists) {
        message.error(`The chasis number ${selectedCar.chasis_no} already exists.`)
        return prev;
      }
      return [...prev, selectedCar];
    });

    form.setFieldsValue({ new_car: "", reload_car: "" });
  };

  // Delete handler
  const handleDelete = (id) => {
    setTableData((prevData) => prevData.filter((item) => item.id !== id));
  };

  const getShippingLine = async () => {
    const response = await dropdownService.fetchShippingLines();
    if (response.success) {
      setShippingLine(response.data);
    }
  };

  const getDestinations = async () => {
    const response = await dropdownService.fetchDestination();
    if (response.success) {
      setDestinations(response.data);
    }
  };

  const getContainerSize = async () => {
    const response = await dropdownService.fetchContainerSizes();
    if (response.success) {
      setContainerSize(response.data);
    }
  };

  const getContainerID = async () => {
    setContainerIDLoad(true)
    // const hide = message.loading('Fetching container ID...', 0);
    const res = await containerService.fetchlatestContainerId();
    // hide();
    setContainerIDLoad(false)
    if (res.success) {
      setContainer_number(res.data.id);
      // message.success('Latest Container ID fetched successfully');
    } else {
      message.error('Failed to fetch Container ID');
    }
  }

  const onFinish = async (val) => {
    setLoading(true);
    const data1 = form.getFieldsValue();
    const additionalData = type === "load" ? { load: "Load" } : type === "save" ? { save: "Save" } : {};
    const merged = { ...data1 };
    const carid = tableData.map((car) => car.id);
    const { new_car, reload_car, ...values } = merged;
    const payload = {
      ...values,
      pull_out_date: values?.pull_out_date?.format("YYYY-MM-DD") ?? null,
      customised_date: values?.customised_date?.format("YYYY-MM-DD") ?? null,
      gate_in_date: values?.gate_in_date?.format("YYYY-MM-DD") ?? null,
      eta_date: values?.eta_date?.format("YYYY-MM-DD") ?? null,
      carid,
      ...additionalData,
    };


    let images = [];
    if (fileList.length > 0) {

      const newFiles = fileList.filter((file) => file.originFileObj);

      if (newFiles.length > 0) {
        images = await awsService.uploadS3Bucket(newFiles, false, payload.load_number, CONTAINERS);
      }
    }

    const data = { ...payload, images };

    const response = await loadingPlanService.addPlan(data);
    setLoading(false);
    if (response?.success ?? false) {
      notification.success({ message: "Successfully added!", description: response.message, Fplacement: "topRight" });
      navigate(type === "save" ? `/operations/loadings/save-containers` : `/operations/loadings`);
    }
  };

  const checkBookingExistsDebounced = useCallback(
    debounce(async (value) => {
      if (!value) {
        setBookingMessage('');
        setBookingExists(null);
        return;
      }
      setBookingLoading(true);
      try {
        const response = await ajaxService.post('/containers/check-booking-number', { booking_number: value });
        if (response?.success) {
          setBookingExists(response.data.exist);
          setBookingMessage(response.message);
        } else {
          setBookingMessage(response?.message || '');
        }
      } catch (err) {
        console.error(err);
        setBookingMessage('Error checking booking number');
      }
      setBookingLoading(false);
    }, 850),
    []
  );

  const filteredOptions = useMemo(() => {
    const selected = tableData.map((car) => car.chasis_no);
    return newCarOptions.filter(
      (opt) => !selected.includes(opt.data.chasis_no)
    );
  }, [newCarOptions, tableData]);

  useEffect(() => {
    const initialBooking = form.getFieldValue("booking_number") || "";
    setBookingInput(initialBooking);
  }, [form]);

  useEffect(() => {
    changeDocumentTitle("Add Load Plan");
    getContainerID();
    getShippingLine();
    getContainerSize();
    getDestinations();
  }, []);

  const SkeletonButton = ({ onClick, children }) => (
    containerIDLoad || bookingLoading
      ? <Skeleton.Input active style={{ width: 220 }} />
      : <Button type="primary" style={{ width: "100%" }} htmlType="submit" onClick={onClick} loading={loading}>
        {children}
      </Button>
  );

  const actionBtn = (
    <Row gutter={24} style={{ marginTop: 40, justifyContent: "flex-end" }}>
      <Col span={6}>
        <SkeletonButton onClick={() => setType("save")}>SAVE</SkeletonButton>
      </Col>
      <Col span={6}>
        <SkeletonButton onClick={() => setType("load")}>LOAD</SkeletonButton>
      </Col>
    </Row>
  );

  return (
    <div
      style={{
        padding: "15px",
        border: "1px solid #ccc",
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Row gutter={26} style={{ marginBottom: 10, padding: "5px" }}>
        <Col span={12}>
          <h1 style={{ fontWeight: "bold" }}>Add Plans</h1>
        </Col>
      </Row>

      <Form
        // requiredMark={false}
        onFinish={onFinish}
        form={form}
        layout="vertical"
        initialValues={{ layout: "vertical" }}
      >
        <Row gutter={24} style={{ padding: 20 }}>
          <Col span={8}>

            <Form.Item
              name="booking_number"
              rules={[{ required: true, message: "Please enter booking number!" }]}
            >
              <div>
                <MaterialLikeInput
                  label="Booking Number"
                  value={bookingInput}
                  loading={bookingLoading}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBookingInput(value); // Update yal input
                    form.setFieldValue("booking_number", value); // Update form
                    setBookingMessage(''); // Clear old messages
                    checkBookingExistsDebounced(value); // Debounced API call
                  }}
                />
                {bookingMessage && (
                  <span className={`text-[11px] ${bookingExists ? 'text-red-600' : 'text-green-600'} mt-1`}>
                    {bookingMessage}
                  </span>
                )}
              </div>
            </Form.Item>

          </Col>
          <Col span={8}>
            <Form.Item
              name="container_number"
            // label={<RenderTitle title="" />}
            // rules={[{ required: true, message: "Please enter container number!" }]}
            >
              <MaterialLikeInput
                label="Container No (This is required for load only)"
                value={form.getFieldValue("container_number")}
                onChange={(e) => form.setFieldValue("container_number", e.target.value)}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="shipping_line"
              rules={[
                {
                  required: true,
                  message: "Please select a shipping line!",
                }
              ]}
            >
              <MaterialLikeSelect
                label="Shipping Line"
                required
                value={form.getFieldValue("shipping_line")}
                onChange={(value) => form.setFieldValue('shipping_line', value)}
                options={shippingLine.map((ship) => ({
                  id: ship.id,
                  value: ship.id,
                  label: ship.name,
                }))}
              />
              {/* <Select placeholder="Shipping Line" showSearch optionFilterProp="children">
                {shippingLine.map((ship) => (
                  <Option key={ship.id} value={ship.id}>
                    {ship.name}
                  </Option>
                ))}
              </Select> */}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="location" rules={[{ required: true, message: "Please enter location!" }]}>

              <MaterialLikeInput
                label="Location"
                required
                disabled={containerIDLoad}
                value={form.getFieldValue("location")}
                onChange={(e) => {
                  const locationValue = e.target.value;
                  form.setFieldValue("location", locationValue);

                  // Generate the load number dynamically
                  const loadNumber = locationValue
                    ? `${locationValue}-${container_number}`
                    : "";

                  form.setFieldValue("load_number", loadNumber);
                }}
                suffix={containerIDLoad && <Spin size="small" />}
              />
              {/* <Input
                placeholder="Location"
                disabled={containerIDLoad}
                onChange={(e) => {
                  // Get the location input value
                  const locationValue = e.target.value;
                  // Append "-93000" to the location value to form the load number
                  const loadNumber = locationValue
                    ? `${locationValue}-${container_number}`
                    : "";
                  // Update the Load Number field dynamically
                  form.setFieldsValue({ load_number: loadNumber });
                }}
                suffix={containerIDLoad && <Spin size="small" />}
              /> */}
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="load_number">
              <MaterialLikeInput
                label="Load Number"
                readOnly
                value={form.getFieldValue("load_number")}
                onChange={(e) => form.setFieldValue("load_number", e.target.value)}
                suffix={containerIDLoad && <Spin size="small" />}
                loading={containerIDLoad}
              />
              {/* <Input
                placeholder="Enter location to get Load Number"
                readOnly
                suffix={containerIDLoad && <Spin size="small" />}
              /> */}
            </Form.Item>
          </Col>

        </Row>
        <Row gutter={24} style={{ padding: 20 }}>
          <Col span={8}>
            <Form.Item
              name="destination"
              rules={[
                {
                  required: true,
                  message: "Please select destination!",
                },
              ]}
            >
              <MaterialLikeSelect
                label="Destination"
                required
                value={form.getFieldValue("destination")}
                onChange={(value) => form.setFieldValue('destination', value)}
                options={destinations.map((destination) => ({
                  id: destination.id,
                  value: destination.name,
                  label: destination.name,
                }))}
              />
              {/* <Select placeholder="Destination" showSearch optionFilterProp="children">
              {destinations.map((destination) => (
                <Option key={destination.id} value={destination.name}>
                  {destination.name}
                </Option>
              ))}
            </Select> */}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="container_size"
              rules={[
                {
                  required: true,
                  message: "Please select a container size!",
                },
              ]}>
              <MaterialLikeSelect
                label="Container Size"
                required
                value={form.getFieldValue("container_size")}
                onChange={(value) => form.setFieldValue('container_size', value)}
                options={containerSize.map((size) => ({
                  id: size.id,
                  value: size.name,
                  label: size.name,
                }))}
              />
              {/* <Select placeholder="Container Size">
                {containerSize.map((size) => (
                  <Select.Option key={size.id} value={size.name}>
                    {size.name}
                  </Select.Option>
                ))}
              </Select> */}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="pull_out_date">
              <MaterialLikeDatePicker
                // required
                label="Pull Out Date (This is required for load only)"
                value={form.getFieldValue("pull_out_date")}
                onChange={(date) => form.setFieldValue("pull_out_date", date)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gate_in_date">
              <MaterialLikeDatePicker
                label="Gate In Date"
                value={form.getFieldValue("gate_in_date")}
                onChange={(date) => form.setFieldValue("gate_in_date", date)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="customised_date">
              <MaterialLikeDatePicker
                label="Customized Date"
                value={form.getFieldValue("customised_date")}
                onChange={(date) => form.setFieldValue("customised_date", date)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="eta_date">
              <MaterialLikeDatePicker
                label="ETA Date"
                value={form.getFieldValue("eta_date")}
                onChange={(date) => form.setFieldValue("eta_date", date)}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <ImageUploader
              fileList={fileList}
              beforeUpload={true}
              setFileList={setFileList}
              setRemovedImages={setRemovedImages}
              handleChange={(info) => {
                const updated = [...info.fileList];
                setFileList(updated);
              }}
              length={100}
            />
          </Col>
          <Col span={24}>
            <Form.Item label="Add New Car" name="new_car">
              <VehicleDropDown CarOptions={filteredOptions} fetchCarOptions={fetchCarOptions} setCarOptions={setNewCarOptions} handleSelect={handleSelect} loading={carLoading} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Add Reload Car" name="reload_car">
              <VehicleDropDown CarOptions={reloadCarOptions} fetchCarOptions={fetchReloadCarOptions} setCarOptions={setReloadCarOptions} handleSelect={handleSelect} loading={carLoading} />
            </Form.Item>
          </Col>
        </Row>

        <Table
          columns={columns}
          pagination={false}
          rowKey="id"
          scroll={{ x: "max-content" }}
          dataSource={tableData}
          style={{ marginTop: "5px" }}
        />

        {actionBtn}
      </Form >
    </div >
  );
}
export default LoadingAdd;
